import React, { useState } from 'react';
import { X, User, Building2, Mail, Phone, Linkedin, ChevronDown, ChevronUp, Clock, Calendar, Briefcase } from 'lucide-react';
import { ReferenceWithAnswers } from '../../api/references';
import { useLanguage } from '../../contexts/LanguageContext';
import useEscapeKey from '../../hooks/useEscapeKey';

interface ReferenceContactModalProps {
  reference: ReferenceWithAnswers;
  isOpen: boolean;
  onClose: () => void;
}

const ReferenceContactModal: React.FC<ReferenceContactModalProps> = ({
  reference,
  isOpen,
  onClose
}) => {
  const { t } = useLanguage();
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  
  // Close modal when Escape key is pressed
  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('candidates.referenceModals.referenceContactModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Contact information */}
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-14 w-14 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-7 w-7 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900">{reference.full_name}</h3>
              <p className="text-sm text-gray-600">{t(`relationships.${reference.relationship_type}`)}</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Primary contact information */}
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <Building2 className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceContactModal.company')}</p>
                <p className="font-medium text-gray-900">{reference.company}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <Mail className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceContactModal.email')}</p>
                <a 
                  href={`mailto:${reference.email}`} 
                  className="font-medium text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {reference.email}
                </a>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <Phone className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceContactModal.phone')}</p>
                {reference.phone ? (
                  <a 
                    href={`tel:${reference.phone}`}
                    className="font-medium text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {reference.phone}
                  </a>
                ) : (
                  <p className="font-medium text-gray-500">{t('candidates.referenceModals.referenceContactModal.notAvailable')}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <Linkedin className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceContactModal.linkedin')}</p>
                {reference.linkedin ? (
                  <a 
                    href={reference.linkedin.startsWith('http') ? reference.linkedin : `https://${reference.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {reference.linkedin}
                  </a>
                ) : (
                  <p className="font-medium text-gray-500">{t('candidates.referenceModals.referenceContactModal.notAvailable')}</p>
                )}
              </div>
            </div>
            
            {/* Additional information (conditionally rendered) */}
            {showMoreInfo && (
              <>
                <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceContactModal.yearsWorked')}</p>
                    <p className="font-medium text-gray-900">{reference.years_worked}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <Briefcase className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceContactModal.department')}</p>
                    <p className="font-medium text-gray-900">{t(`departments.${reference.department}`)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <User className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceContactModal.seniority')}</p>
                    <p className="font-medium text-gray-900">{reference.seniority}</p>
                  </div>
                </div>
              </>
            )}
            
            {/* Toggle button */}
            <button 
              onClick={() => setShowMoreInfo(!showMoreInfo)}
              className="w-full flex items-center justify-center py-2.5 px-4 text-sm text-blue-600 hover:text-blue-800 transition-colors rounded-lg"
            >
              <span className="font-medium">
                {showMoreInfo 
                  ? t('candidates.referenceModals.referenceContactModal.showLess') 
                  : t('candidates.referenceModals.referenceContactModal.viewMoreInfo')
                }
              </span>
              {showMoreInfo ? (
                <ChevronUp className="h-4 w-4 ml-1.5" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1.5" />
              )}
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors text-gray-700 font-medium"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceContactModal;